import { IndicationAnswer, SurveyChoiceComponent } from '@abiglobalhealth/go-types'
import { CheckedIcon } from '@components/icons/Checked'
import { Dispatch, SetStateAction, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useButtonClick } from '@/hooks/useButtonClick'
import { useExitLogicModal } from '@/hooks/useExitLogicModal'
import { useIndications } from '@/hooks/useIndications'

type SurveyChoiceProps = Omit<SurveyChoiceComponent, 'componentType'> & {
  selected: string | null
  setSelected: Dispatch<SetStateAction<string | null>>
  tabIndex: number
  initialSelected: IndicationAnswer[]
  groupError?: string
}

export const SurveyChoice = ({
  id,
  textKey,
  buttonAction,
  selected,
  setSelected,
  tabIndex,
  initialSelected,
  groupError,
  isExitLogic
}: SurveyChoiceProps) => {
  const { t } = useTranslation()
  const { showExitLogicModal } = useExitLogicModal()
  const { handleButtonClick } = useButtonClick()
  const { indications } = useIndications()

  const handleChange = () => {
    if (isExitLogic) return showExitLogicModal()

    setSelected(id)
  }

  useEffect(() => {
    if (indications?.length < 1 || !buttonAction || selected !== id) return

    handleButtonClick(buttonAction)
  }, [indications])

  const border = groupError ? 'border-error' : 'border-gray'

  return (
    <span className="relative" data-testid={`${id}-container`}>
      <input
        id={id}
        tabIndex={tabIndex}
        data-testid={`${id}-input`}
        type="checkbox" // This needs to be checkbox in order to be able to use the tabindex navigation with tab key
        className="peer absolute h-0 w-0 opacity-0"
        checked={selected === id || (!selected && Boolean(initialSelected.find((is) => is.id === id)))}
        onChange={handleChange}
      />

      <label
        htmlFor={id}
        className={`text-primary peer-checked:border-primary peer-checked:bg-primary-light peer-checked:text-primary peer-focus-visible:outline-primary flex w-full cursor-pointer justify-center rounded-full border border-transparent bg-white px-8 py-4 text-center font-bold uppercase shadow-sm peer-checked:border-2 peer-checked:font-bold peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 ${border}`}
      >
        {t(textKey)}
      </label>

      <CheckedIcon customClassName="absolute top-1/2 right-4 hidden w-4 -translate-y-1/2 transform peer-checked:block fill-primary" />
    </span>
  )
}
